import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Users, BookOpen, GraduationCap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Batches() {
    const [batches, setBatches] = useState([]);
    const [courses, setCourses] = useState([]);
    const [faculty, setFaculty] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", start_date: "" });
    const [edit, setEdit] = useState(null);
    const { user } = useAuth();

    const load = async () => {
        const [b, c, f] = await Promise.all([
            api.get("/batches"), api.get("/courses"), api.get("/faculty?status=active"),
        ]);
        setBatches(b.data.items);
        setCourses(c.data.items);
        setFaculty(f.data.items);
    };
    useEffect(() => { load(); }, []);

    const create = async (e) => {
        e.preventDefault();
        try {
            await api.post("/batches", form);
            toast.success("Batch created");
            setOpen(false); setForm({ name: "", start_date: "" });
            load();
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    };

    const toggleCourse = async (batch, courseId) => {
        const ids = new Set(batch.course_ids || []);
        if (ids.has(courseId)) ids.delete(courseId); else ids.add(courseId);
        await api.put(`/batches/${batch.id}`, { course_ids: Array.from(ids) });
        toast.success("Saved");
        load();
        setEdit(null);
    };

    const toggleFaculty = async (batch, facultyId) => {
        const ids = new Set(batch.faculty_ids || []);
        if (ids.has(facultyId)) ids.delete(facultyId); else ids.add(facultyId);
        await api.put(`/batches/${batch.id}`, { faculty_ids: Array.from(ids) });
        toast.success("Saved");
        load();
        setEdit(null);
    };

    return (
        <div className="space-y-6" data-testid="batches-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Manage</div>
                    <h1 className="font-display text-3xl lg:text-4xl mt-1">Batches</h1>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    {user?.role !== "FACULTY" && (<DialogTrigger asChild>
                        <Button className="mx-btn-primary" data-testid="add-batch-btn"><Plus className="h-4 w-4 mr-2" /> New batch</Button>
                    </DialogTrigger>)}
                    <DialogContent>
                        <DialogHeader><DialogTitle>Create batch</DialogTitle></DialogHeader>
                        <form onSubmit={create} className="space-y-4">
                            <div><Label>Name</Label><Input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Digital Marketing Aug 2027" data-testid="batch-name-input" /></div>
                            <div><Label>Start date</Label><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} data-testid="batch-date-input" /></div>
                            <DialogFooter><Button type="submit" className="mx-btn-primary" data-testid="batch-create-submit">Create</Button></DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                {batches.map(b => {
                    const linkedCourses = courses.filter(c => (b.course_ids || []).includes(c.id));
                    const linkedFaculty = faculty.filter(f => (b.faculty_ids || []).includes(f.id));
                    return (
                        <Card key={b.id} className="mx-card" data-testid={`batch-card-${b.id}`}>
                            <CardContent className="p-5 space-y-4">
                                <div>
                                    <h3 className="font-bold text-lg">{b.name}</h3>
                                    <div className="text-xs text-muted-foreground mt-1">Status: {b.status}</div>
                                </div>
                                <div className="grid grid-cols-3 gap-2 text-center">
                                    <div className="bg-[#F0EFFF] dark:bg-[#1C1A2E] rounded-lg p-2">
                                        <Users className="h-4 w-4 mx-auto text-[#726AF0]" />
                                        <div className="font-display text-lg mt-1">{b.student_count || 0}</div>
                                        <div className="text-[10px] uppercase font-bold text-muted-foreground">Students</div>
                                    </div>
                                    <div className="bg-[#F0EFFF] dark:bg-[#1C1A2E] rounded-lg p-2">
                                        <BookOpen className="h-4 w-4 mx-auto text-[#E64AA1]" />
                                        <div className="font-display text-lg mt-1">{linkedCourses.length}</div>
                                        <div className="text-[10px] uppercase font-bold text-muted-foreground">Courses</div>
                                    </div>
                                    <div className="bg-[#F0EFFF] dark:bg-[#1C1A2E] rounded-lg p-2">
                                        <GraduationCap className="h-4 w-4 mx-auto text-[#FFA81B]" />
                                        <div className="font-display text-lg mt-1">{linkedFaculty.length}</div>
                                        <div className="text-[10px] uppercase font-bold text-muted-foreground">Faculty</div>
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase font-bold text-muted-foreground mb-1.5">Courses</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {linkedCourses.map(c => <Badge key={c.id} variant="secondary" className="text-[10px]">{c.title}</Badge>)}
                                        {linkedCourses.length === 0 && <span className="text-xs text-muted-foreground">None assigned</span>}
                                    </div>
                                </div>
                                <Button size="sm" variant="outline" className="w-full rounded-xl" onClick={() => setEdit(b)} data-testid={`batch-manage-${b.id}`}>Manage</Button>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
                    <DialogHeader><DialogTitle>{edit?.name}</DialogTitle></DialogHeader>
                    {edit && (
                        <div className="space-y-6">
                            <div>
                                <h4 className="font-semibold text-sm mb-2">Assign courses</h4>
                                <div className="grid gap-2">
                                    {courses.map(c => (
                                        <label key={c.id} className="flex items-center gap-3 p-3 border border-border rounded-xl cursor-pointer hover:bg-muted" data-testid={`batch-toggle-course-${c.id}`}>
                                            <input type="checkbox" checked={(edit.course_ids || []).includes(c.id)} onChange={() => toggleCourse(edit, c.id)} className="accent-[#726AF0]" />
                                            <div className="flex-1"><div className="font-semibold text-sm">{c.title}</div><div className="text-xs text-muted-foreground">{c.category}</div></div>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <h4 className="font-semibold text-sm mb-2">Assign faculty</h4>
                                <div className="grid gap-2">
                                    {faculty.map(f => (
                                        <label key={f.id} className="flex items-center gap-3 p-3 border border-border rounded-xl cursor-pointer hover:bg-muted" data-testid={`batch-toggle-faculty-${f.id}`}>
                                            <input type="checkbox" checked={(edit.faculty_ids || []).includes(f.id)} onChange={() => toggleFaculty(edit, f.id)} className="accent-[#726AF0]" />
                                            <div className="flex-1"><div className="font-semibold text-sm">{f.name}</div><div className="text-xs text-muted-foreground">{f.email}</div></div>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
